import { useCallback, useMemo, useRef } from "react"

import {
  DEFAULT_ROUTE_OPTIMIZATION_CONFIG,
  RouteDeviationTracker,
  type Point,
  type RouteDeviationResult,
  type RouteOptimizationConfig,
} from "../utils/polyline-snap"

export interface UseRouteOptimizationProps {
  /** Custom configuration for route optimization */
  config?: Partial<RouteOptimizationConfig>
  /** Whether to enable route optimization */
  enabled?: boolean
  /** Debug mode for logging */
  enableDebug?: boolean
}

export interface RouteOptimizationHookResult {
  /** Check if route should be refetched based on current position */
  shouldRefetchRoute: (
    currentPosition: Point,
    currentRoute: Point[],
    currentSpeed?: number,
    currentBearing?: number
  ) => RouteDeviationResult

  /** Mark that a route refetch was successfully completed */
  markRefetchCompleted: () => void

  /** Reset the tracker (useful when starting a new route) */
  resetTracker: () => void

  /** Get debug information about the tracker state */
  getDebugInfo: () => any

  /** Get the current tracker configuration */
  getConfig: () => RouteOptimizationConfig
}

/**
 * Hook for intelligent route optimization that prevents unnecessary API calls
 * by analyzing driver deviation patterns and only refetching when needed.
 *
 * This addresses the user's request: "not to fetch the new polyline data when
 * driver is following the current polyline route, to limit the call to the
 * route api as least as possible."
 */
export function useRouteOptimization({
  config = {},
  enabled = true,
  enableDebug = false,
}: UseRouteOptimizationProps = {}): RouteOptimizationHookResult {
  // Merge user config with defaults
  const optimizationConfig = useMemo(
    () => ({
      ...DEFAULT_ROUTE_OPTIMIZATION_CONFIG,
      ...config,
    }),
    [config]
  )

  // Create persistent tracker instance
  const trackerRef = useRef<RouteDeviationTracker | null>(null)

  // Initialize tracker with current config
  if (!trackerRef.current) {
    trackerRef.current = new RouteDeviationTracker(optimizationConfig)
  }

  // Update tracker config if it changed
  if (
    trackerRef.current &&
    JSON.stringify(trackerRef.current.getDebugInfo().config) !==
      JSON.stringify(optimizationConfig)
  ) {
    trackerRef.current = new RouteDeviationTracker(optimizationConfig)
  }

  const shouldRefetchRoute = useCallback(
    (
      currentPosition: Point,
      currentRoute: Point[],
      currentSpeed?: number,
      currentBearing?: number
    ): RouteDeviationResult => {
      if (!enabled || !trackerRef.current) {
        // When disabled, always allow refetching (default behavior)
        return {
          isOnRoute: true,
          distanceFromRoute: 0,
          deviationSeverity: "none",
          shouldRefetchRoute: true, // Allow all refetches when disabled
          confidenceScore: 100,
          consecutiveDeviations: 0,
        }
      }

      const result = trackerRef.current.analyzeDeviation(
        currentPosition,
        currentRoute,
        currentSpeed,
        currentBearing
      )

      if (enableDebug) {
        console.log("🛣️  Route Optimization Analysis:", {
          position: currentPosition,
          route: `${currentRoute.length} points`,
          speed: currentSpeed ? `${currentSpeed.toFixed(1)} km/h` : "unknown",
          bearing: currentBearing ? `${currentBearing.toFixed(1)}°` : "unknown",
          result: {
            isOnRoute: result.isOnRoute,
            distanceFromRoute: `${result.distanceFromRoute.toFixed(1)}m`,
            severity: result.deviationSeverity,
            shouldRefetch: result.shouldRefetchRoute,
            confidence: `${result.confidenceScore.toFixed(1)}%`,
            consecutiveDeviations: result.consecutiveDeviations,
            timeOffRoute: result.timeOffRoute
              ? `${(result.timeOffRoute / 1000).toFixed(1)}s`
              : "none",
          },
        })
      }

      return result
    },
    [enabled, enableDebug]
  )

  const markRefetchCompleted = useCallback(() => {
    if (trackerRef.current) {
      if (enableDebug) {
        console.log("✅ Route refetch completed - resetting tracker")
      }
      // Reset is automatically called by the tracker when shouldRefetchRoute returns true
      // But we can call it manually here for explicit resets
    }
  }, [enableDebug])

  const resetTracker = useCallback(() => {
    if (trackerRef.current) {
      trackerRef.current.reset()
      if (enableDebug) {
        console.log("🔄 Route tracker reset")
      }
    }
  }, [enableDebug])

  const getDebugInfo = useCallback(() => {
    return trackerRef.current?.getDebugInfo() || {}
  }, [])

  const getConfig = useCallback(() => {
    return optimizationConfig
  }, [optimizationConfig])

  return {
    shouldRefetchRoute,
    markRefetchCompleted,
    resetTracker,
    getDebugInfo,
    getConfig,
  }
}
